import dayjs from "dayjs";
import mapValues from "lodash/mapValues";
import styled, { css } from "styled-components";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { ChangeValue, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { InfoIcon } from "pro-features/components/BoxTitle/src/BoxTitle";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, minimumValueChangeFilter } from "app/filters/ngFilters";
import { useDisplayAdvertiserPublisherDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";
import type { FlattenedEntitiesData } from "app/pages/digital-marketing/ad-intelligence/common/overview/hooks/useActivityBreakdownData";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import {
    FlexColumn,
    FlexRow,
    LeftFlexRow,
    RightFlexRow,
} from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { TOP_CAMPAIGNS_TABLE_ID } from "../../advertisers/overview/components/RecentCampaignsTable";
import { TABLE_ID as TOP_ADVERTISERS_PUBLISHERS_TABLE_ID } from "../advertisersPublishers/TopAdvertisersPublishers";
import type { AdIntelligenceController } from "../shared/types/types";
import { NA_VALUE } from "../utils/utils";

import { TOP_ADS_TABLE_ID } from "./RecentAdsGallery";

const RowsContainer = styled(FlexColumn)`
    row-gap: 8px;
`;

const Row = styled(FlexRow)`
    justify-content: space-between;

    &:not(:last-child) {
        border-bottom: 1px solid ${colorsPalettes.carbon[100]};
        padding-bottom: 4px;
    }
`;

const RowTitle = styled(LeftFlexRow)`
    column-gap: 4px;
`;

const Title = styled(I18n)<{ onClick }>`
    ${setFontVariant("input2", colorsPalettes.carbon[500])};
    ${({ onClick }) =>
        onClick &&
        css`
            cursor: pointer;
            &:hover {
                ${setFontVariant("input2", colorsPalettes.blue[500])};
            }
        `}
`;

const SubLabel = styled.div`
    ${setFontVariant("queryBar1", colorsPalettes.carbon[300])};
    margin-top: 4px;
    margin-bottom: 4px;
`;

const RowValue = styled(RightFlexRow)`
    column-gap: 8px;
`;

const ChangeValueStyled = styled(ChangeValue)`
    width: 80px;
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
`;

const ChangeValuePlaceholder = styled.div`
    width: 80px;
`;

const Count = styled.span`
    ${setFontVariant("h5", colorsPalettes.carbon[400])};
`;

const entitiesLoadingState = (
    <Stack gap="16px">
        <PixelPlaceholderLoader
            height={24}
            width="100%"
        />
        <PixelPlaceholderLoader
            height={24}
            width="100%"
        />
        <PixelPlaceholderLoader
            height={24}
            width="100%"
        />
    </Stack>
);

export const elementIdToEventNameMap: Record<string, string> = {
    [TOP_CAMPAIGNS_TABLE_ID]: "Top Campaigns Table",
    [TOP_ADS_TABLE_ID]: "Top Ads Table",
    [TOP_ADVERTISERS_PUBLISHERS_TABLE_ID]: "Top Publishers Table",
};

interface IPerformanceEntitiesWidgetProps<T extends AdIntelligenceController> {
    data: FlattenedEntitiesData<T>;
    isLoading: boolean;
    controller: T;
    isWindow: boolean;
}

export const PerformanceEntitiesWidget = <T extends AdIntelligenceController>({
    data,
    isLoading,
    controller,
    isWindow,
}: IPerformanceEntitiesWidgetProps<T>) => {
    const isAdvertiserCtrl = controller === "advertiser";
    const thirdRowEntity: `${AdIntelligenceController}s` = isAdvertiserCtrl ? "publishers" : "advertisers";
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { duration } = params;
    const durationForTooltip = mapValues(
        DurationService.getDurationData(duration).forTooltip as dateForTooltip,
        (item) => {
            return decodeURIComponent(item);
        },
    );

    // Check if we should show the sublabel
    const shouldShowSubLabel = !isLoading && data?.startDate;
    const subLabelText = shouldShowSubLabel
        ? (() => {
              const startDateFormatted = dayjs(data.startDate).format(shortMonthAndYear);
              const endDateFormatted = durationForTooltip.to;
              return startDateFormatted === endDateFormatted
                  ? startDateFormatted
                  : `${startDateFormatted} - ${endDateFormatted}`;
          })()
        : null;

    const getDataRow = (
        label: string,
        tooltip: string,
        showChange: boolean,
        count?: number,
        change?: number,
        onClick?: VoidFunction,
        showSubLabel = true,
    ) => (
        <Row data-automation-label={label}>
            <RowTitle>
                <FlexColumn>
                    <LeftFlexRow style={{ columnGap: "4px" }}>
                        <Title onClick={onClick}>{label}</Title>
                        <Tooltip
                            placement="top"
                            content={translate(tooltip)}
                        >
                            <span>
                                <InfoIcon iconName="info" />
                            </span>
                        </Tooltip>
                    </LeftFlexRow>
                    {showSubLabel && shouldShowSubLabel ? <SubLabel>{subLabelText}</SubLabel> : null}
                </FlexColumn>
            </RowTitle>
            <RowValue>
                <Count data-automation-count={count}>{count ? abbrNumberFilter()(count) : NA_VALUE}</Count>
                {!showChange ? null : change ? (
                    <Tooltip
                        placement="top"
                        content={
                            isWindow
                                ? translate("ad.intelligence.overview.performance.widget.window.change.tooltip")
                                : translate(
                                      "ad.intelligence.overview.performance.entities.widget.change.tooltip",
                                      durationForTooltip,
                                  )
                        }
                    >
                        <FlexRow>
                            <ChangeValueStyled
                                value={!change ? NA_VALUE : minimumValueChangeFilter()(change, 1)}
                                isDecrease={change < 0}
                            />
                        </FlexRow>
                    </Tooltip>
                ) : (
                    <ChangeValuePlaceholder></ChangeValuePlaceholder>
                )}
            </RowValue>
        </Row>
    );

    const scrollIntoView = (elementId: string) => {
        const element = document.getElementById(elementId);
        const pageElement = document.getElementsByClassName("sw-layout-scrollable-element")?.[0];
        if (element && pageElement) {
            allTrackers.dispatchTrackEvent("Internal Link", "click", elementIdToEventNameMap[elementId]);
            pageElement.scroll({ top: element.offsetTop, behavior: "smooth" });
        }
    };

    const shouldShowChangeColumn =
        !isLoading && (!!data.campaigns.change || !!data.creatives.change || !!data[thirdRowEntity]?.change);

    return (
        <RowsContainer>
            {isLoading ? (
                entitiesLoadingState
            ) : (
                <>
                    {getDataRow(
                        "ad.intelligence.overview.performance.entities.widget.campaigns.title",
                        `ad.intelligence.${controller}.overview.performance.entities.widget.campaigns.tooltip`,
                        shouldShowChangeColumn,
                        data.campaigns.count,
                        data.campaigns.change,
                        isAdvertiserCtrl ? scrollIntoView.bind(null, TOP_CAMPAIGNS_TABLE_ID) : null,
                    )}
                    {getDataRow(
                        "ad.intelligence.overview.performance.entities.widget.creatives.title",
                        `ad.intelligence.${controller}.overview.performance.entities.widget.creatives.tooltip`,
                        shouldShowChangeColumn,
                        data.creatives.count,
                        data.creatives.change,
                        scrollIntoView.bind(null, TOP_ADS_TABLE_ID),
                    )}
                    {getDataRow(
                        `ad.intelligence.overview.performance.entities.widget.${thirdRowEntity}.title`,
                        `ad.intelligence.${controller}.overview.performance.entities.widget.${thirdRowEntity}.tooltip`,
                        shouldShowChangeColumn,
                        data[thirdRowEntity]?.count ?? 0,
                        data[thirdRowEntity]?.change ?? 0,
                        scrollIntoView.bind(null, TOP_ADVERTISERS_PUBLISHERS_TABLE_ID),
                        false,
                    )}
                </>
            )}
        </RowsContainer>
    );
};
